import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router";
import {
  generateDisplayErrorMessage,
  handleValidationErrors,
  mapAPIErrorToFormError,
  request,
  responseHandler,
} from "../helpers/utilities";

import * as Sentry from "@sentry/browser";
import { Select, Spin, message } from "antd";
import { AxiosResponse } from "axios";
import Card from "../components/Card";
import BaseForm from "../components/Form/BaseForm";

import { FormInstance } from "antd/lib/form";
import debounce from "lodash.debounce";
import {
  APIError,
  Department,
  FormFieldConfig,
  Role,
  User,
} from "../helpers/shared-interfaces";
import { CREATE_USER } from "../gql/users";
import { GET_ROLES } from "../gql/roles";
import { GET_DEPARTMENTS } from "../gql/departments";
import { DEBOUNCE_TIME } from "../helpers/constants";


const { Option } = Select;

export function SelectOptionFactory(
  data: Role[] | Department[],
): JSX.Element[] {
  return data.map((item: Role | Department): JSX.Element => {
    return (
      <Option key={item.id} value={item.id}>
        {item.name}
      </Option>
    );
  });
}

interface State {
  name: string;
  isLoading: boolean;
  email: string;
  departmentId: string;
  roleId: string;
  roles: Role[];
  departments: Department[];
  search: string;
}

export default class CreateUser extends React.Component<
  RouteComponentProps,
  State
> {
  reference: RefObject<FormInstance>;
  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      name: "",
      email: "",
      departmentId: "",
      roleId: "",
      isLoading: true,
      roles: [],
      departments: [],
      search: "",
    };
    this.reference = React.createRef<FormInstance>();

    this.handleSelectChange = this.handleSelectChange.bind(this);
    this.redirectToAllUsersPage = this.redirectToAllUsersPage.bind(this);
    this.getDepartmentsAndRoles = this.getDepartmentsAndRoles.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.createUser = this.createUser.bind(this);
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  handleInputChange(
    key: string,
  ): (e: React.ChangeEvent<HTMLInputElement>) => void {
    return (e: React.ChangeEvent<HTMLInputElement>): void => {
      this.setState({ ...this.state, [key.toString()]: e.target.value });
    };
  }

  handleSelectChange(key: string): (value: string) => void {
    return (value): void => {
      this.setState({ ...this.state, [key.toString()]: value });
    };
  }

  async createUser(): Promise<void> {
    interface Response {
      data?: {
        createUser: {
          user: User;
          errors: APIError[];
        };
      };
    }

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields([
        "name",
        "email",
        "departmentId",
        "roleId",
      ]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    try {
      const response: AxiosResponse<Response> = await request({
        query: CREATE_USER,
        variables: data,
      });

      const { err, handled } = responseHandler<Response>(
        response,
        "createUser",
      );

      this.setState({ isLoading: false });

      if (handled) {
        message.error("An Error Occurred. Your request couldn't be processed.");
        return;
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        message.success("User created sucessfully");
        this.redirectToAllUsersPage();
      }
    } catch (error) {
      this.setState({ isLoading: false });

      message.error(`Failed to create user - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  }

  redirectToAllUsersPage(): void {
    this.props.history.push("/users");
  }

  getAllRoles = (): Promise<AxiosResponse> => {
    return request({
      query: GET_ROLES,
      variables: {
        page: 1,
        q: this.state.search,
      },
    });
  };

  getAllDepartments = (): Promise<AxiosResponse> => {
    return request({
      query: GET_DEPARTMENTS,
    });
  };

  async getDepartmentsAndRoles(): Promise<void> {
    this.setState({ isLoading: true });
    try {
      const [roles, departments]: any = await Promise.all([
        this.getAllRoles(),
        this.getAllDepartments(),
      ]);
      this.setState({
        isLoading: false,
        roles: roles.data.data.roles.results,
        departments: departments.data.data.departments,
      });
    } catch (e) {}
  }

  handleSearchInput = async (search: string): Promise<void> => {
    this.setState({ search });
    await this.triggerDelayedSearch();
  };

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    const newRoles = await this.getAllRoles();
    this.setState({
      roles: newRoles.data.data.roles.results,
    });
  }, DEBOUNCE_TIME);

  componentDidMount(): void {
    this.getDepartmentsAndRoles();
  }

  render(): JSX.Element | null {
    const { isLoading, roles, departments } = this.state;
    const fields: FormFieldConfig[] = [
      {
        name: "name",
        rule: "string",
        inputType: "string",
        required: true,
        label: "Name",
        placeholder: "Name",
        colConfig: {
          span: 7,
        },
      },
      {
        name: "email",
        rule: "email",
        inputType: "email",
        required: true,
        label: "Email",
        placeholder: "Email",
        colConfig: {
          span: 7,
          offset: 1,
        },
      },
      {
        name: "departmentId",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Department",
        placeholder: "Department",
        selectOptions: departments,
        colConfig: {
          span: 7,
          offset: 1,
        },
      },
      {
        name: "roleId",
        rule: "string",
        inputType: "search",
        required: true,
        label: "Role",
        placeholder: "Role",
        selectOptions: roles,
        changeHandler: this.handleSearchInput,
        colConfig: {
          span: 7,
        },
      },
      {
        id: "createUser",
        name: "createUserButton",
        inputType: "button",
        value: "Save",
        required: false,
        className: "base-button",
        elementStyle: { position: "relative", top: "3px" },
        colConfig: {
          span: 24,
        },
        handleClick: this.createUser,
      },
    ];

    return (
      <Card header="NEW USER">
        <div>
          <Spin spinning={isLoading}>
            <BaseForm
              layout="vertical"
              reference={this.reference}
              fieldsToHide={[]}
              fields={fields}
            />
          </Spin>
        </div>
      </Card>
    );
  }
}
